import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, TestRunWithProgress } from '../services/api';
import { TestExecutionWithDetails } from '../types';
import TestExecutionModal from '../components/TestExecutionModal';
import { emailToUsername } from '../utils/formatters';
import './TestRunDetailPage.css';

const PAGE_SIZE = 10;

const TestRunDetailPage: React.FC = () => {
  const { testRunId } = useParams<{ testRunId: string }>();
  const navigate = useNavigate();
  const [testRun, setTestRun] = useState<TestRunWithProgress | null>(null);
  const [executions, setExecutions] = useState<TestExecutionWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [selectedExecution, setSelectedExecution] = useState<TestExecutionWithDetails | null>(null);
  const [showExecutionModal, setShowExecutionModal] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (testRunId) {
      loadTestRun();
      loadExecutions();
    }
  }, [testRunId, statusFilter]);

  const loadTestRun = async () => {
    try {
      const run = await api.getTestRun(testRunId!);
      setTestRun(run);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load test run');
    }
  };

  const loadExecutions = async () => {
    try {
      setLoading(true);
      const execs = await api.getTestRunExecutions(testRunId!, statusFilter || undefined);
      setExecutions(execs);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load executions');
    } finally {
      setLoading(false);
    }
  };

  const handleStartRun = async () => {
    try {
      await api.startTestRun(testRunId!);
      await loadTestRun();
    } catch (err: any) {
      setError(err.message || 'Failed to start test run');
    }
  };

  const handleCloseRun = async () => {
    if (!window.confirm('Are you sure you want to close this test run? It will become immutable.')) return;
    try {
      await api.closeTestRun(testRunId!);
      await loadTestRun();
    } catch (err: any) {
      setError(err.message || 'Failed to close test run');
    }
  };

  const handleExecuteTestCase = (execution: TestExecutionWithDetails) => {
    setSelectedExecution(execution);
    setShowExecutionModal(true);
  };

  const handleExecutionComplete = () => {
    setShowExecutionModal(false);
    setSelectedExecution(null);
    loadTestRun();
    loadExecutions();
  };

  const getRunStatusClass = (status: string) => {
    switch (status) {
      case 'IN_PROGRESS': return 'run-status-inprogress';
      case 'COMPLETED':   return 'run-status-completed';
      case 'ABORTED':     return 'run-status-aborted';
      default:            return 'run-status-notstarted';
    }
  };

  const getRunStatusLabel = (status: string) => {
    switch (status) {
      case 'IN_PROGRESS': return 'IN PROGRESS';
      case 'NOT_STARTED': return 'NOT STARTED';
      default: return status;
    }
  };

  const getExecStatusClass = (status: string) => {
    switch (status) {
      case 'PASS':    return 'exec-pass';
      case 'FAIL':    return 'exec-fail';
      case 'BLOCKED': return 'exec-blocked';
      default:        return 'exec-notrun';
    }
  };

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateLine = (dateStr?: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
      + ' · ' + new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading && !testRun) {
    return <div className="trd-page"><div className="trd-loading">Loading...</div></div>;
  }
  if (!testRun) {
    return <div className="trd-page"><div className="trd-error">Test run not found</div></div>;
  }

  const isImmutable = testRun.status === 'COMPLETED' || testRun.status === 'ABORTED';

  // Progress bar segments
  const total = testRun.total_cases || 1;
  const passW    = Math.round((testRun.pass_count / total) * 100);
  const failW    = Math.round((testRun.fail_count / total) * 100);
  const blockedW = Math.round((testRun.blocked_count / total) * 100);
  const notRunW  = Math.max(0, 100 - passW - failW - blockedW);

  // Filter + search + paginate
  const filtered = executions.filter(e => {
    const matchSearch = !searchTerm ||
      e.test_case_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.test_case_display_id?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchSearch;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const STATUS_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: 'PASS', label: 'Pass' },
    { value: 'FAIL', label: 'Fail' },
    { value: 'BLOCKED', label: 'Blocked' },
    { value: 'NOT_RUN', label: 'Not Run' },
  ];

  const currentStatusLabel = STATUS_OPTIONS.find(o => o.value === statusFilter)?.label || 'All Statuses';

  return (
    <div className="trd-page">
      {/* Back link */}
      <button className="trd-back" onClick={() => navigate('/test-runs')}>
        ← Back to Test Runs
      </button>

      {error && <div className="trd-error-banner">{error}<button onClick={() => setError(null)}>×</button></div>}

      {/* Title row */}
      <div className="trd-title-row">
        <div className="trd-title-left">
          <h1 className="trd-title">{testRun.name}</h1>
          <span className={`trd-run-status ${getRunStatusClass(testRun.status)}`}>
            {getRunStatusLabel(testRun.status)}
          </span>
        </div>
        <div className="trd-title-actions">
          {testRun.status === 'NOT_STARTED' && (
            <button className="trd-btn-start" onClick={handleStartRun}>▶ Start Execution</button>
          )}
          {testRun.status === 'IN_PROGRESS' && (
            <button className="trd-btn-close" onClick={handleCloseRun}>Close Test Run</button>
          )}
        </div>
      </div>

      {/* Info + Progress row */}
      <div className="trd-top-row">
        {/* Execution context */}
        <div className="trd-context-card">
          <div className="trd-context-label">EXECUTION CONTEXT</div>
          <div className="trd-context-row">
            <span className="trd-ctx-key">Build Version</span>
            <span className="trd-ctx-val trd-ctx-mono">{testRun.build_version || '—'}</span>
          </div>
          <div className="trd-context-row">
            <span className="trd-ctx-key">Environment</span>
            <span className="trd-ctx-val trd-ctx-bold">{testRun.environment || '—'}</span>
          </div>
          <div className="trd-context-row">
            <span className="trd-ctx-key">Created By</span>
            <div className="trd-ctx-val">
              <span className="trd-ctx-bold">{emailToUsername(testRun.created_by)}</span>
              <span className="trd-ctx-sub">{formatDateLine(testRun.created_at)}</span>
            </div>
          </div>
          {testRun.started_at && (
            <div className="trd-context-row">
              <span className="trd-ctx-key">Started By</span>
              <div className="trd-ctx-val">
                <span className="trd-ctx-bold">{testRun.started_by ? emailToUsername(testRun.started_by) : 'System'}</span>
                <span className="trd-ctx-sub">{formatDateLine(testRun.started_at)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Global progress */}
        <div className="trd-progress-card">
          <div className="trd-progress-top">
            <span className="trd-progress-label">GLOBAL PROGRESS</span>
            <span className="trd-progress-pct">
              <strong>{Math.round(testRun.progress_percentage)}%</strong>
              <span className="trd-progress-complete"> Complete</span>
            </span>
          </div>

          {/* Segmented progress bar */}
          <div className="trd-progress-bar">
            {passW > 0    && <div className="trd-seg trd-seg-pass"    style={{ width: `${passW}%` }} />}
            {failW > 0    && <div className="trd-seg trd-seg-fail"    style={{ width: `${failW}%` }} />}
            {blockedW > 0 && <div className="trd-seg trd-seg-blocked" style={{ width: `${blockedW}%` }} />}
            {notRunW > 0  && <div className="trd-seg trd-seg-notrun"  style={{ width: `${notRunW}%` }} />}
          </div>

          {/* Metric pills */}
          <div className="trd-metrics">
            <div className="trd-metric">
              <span className="trd-metric-label">TOTAL</span>
              <span className="trd-metric-val">{testRun.total_cases}</span>
            </div>
            <div className="trd-metric trd-metric-pass">
              <span className="trd-metric-label">PASSED</span>
              <span className="trd-metric-val">{testRun.pass_count}</span>
            </div>
            <div className="trd-metric trd-metric-fail">
              <span className="trd-metric-label">FAILED</span>
              <span className="trd-metric-val">{testRun.fail_count}</span>
            </div>
            <div className="trd-metric trd-metric-blocked">
              <span className="trd-metric-label">BLOCKED</span>
              <span className="trd-metric-val">{testRun.blocked_count}</span>
            </div>
            <div className="trd-metric trd-metric-notrun">
              <span className="trd-metric-label">NOT RUN</span>
              <span className="trd-metric-val">{testRun.not_run_count}</span>
            </div>
            <div className="trd-metric trd-metric-rate">
              <span className="trd-metric-label">PASS RATE</span>
              <span className="trd-metric-val">{testRun.pass_percentage.toFixed(1)}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Executions table */}
      <div className="trd-table-card">
        <div className="trd-table-header">
          <h2 className="trd-table-title">Test Execution Details</h2>
          <div className="trd-table-controls">
            {/* Status filter dropdown */}
            <div
              className="trd-status-filter"
              onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShowStatusDropdown(false); }}
            >
              <button
                className="trd-filter-btn"
                onClick={() => setShowStatusDropdown(v => !v)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                </svg>
                {currentStatusLabel}
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M6 9l6 6 6-6"/>
                </svg>
              </button>
              {showStatusDropdown && (
                <div className="trd-filter-dropdown">
                  {STATUS_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      className={`trd-filter-option${statusFilter === opt.value ? ' selected' : ''}`}
                      onClick={() => { setStatusFilter(opt.value); setShowStatusDropdown(false); setPage(1); }}
                    >
                      {statusFilter === opt.value && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Search */}
            <div className="trd-search-wrap">
              <svg className="trd-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="trd-search"
                type="text"
                placeholder="Search test cases..."
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="trd-loading">Loading test cases...</div>
        ) : (
          <>
            <table className="trd-table">
              <thead>
                <tr>
                  <th>TEST CASE &amp; ID</th>
                  <th>STATUS</th>
                  <th>EXECUTED BY</th>
                  <th>EXECUTED AT</th>
                  <th>DEFECT</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="trd-empty">No test cases found</td>
                  </tr>
                ) : paginated.map(execution => (
                  <tr key={execution.id}>
                    <td>
                      <div className="trd-tc-name">{execution.test_case_description}</div>
                      {execution.test_case_display_id && (
                        <div className="trd-tc-id">{execution.test_case_display_id}</div>
                      )}
                    </td>
                    <td>
                      <span className={`trd-exec-badge ${getExecStatusClass(execution.execution_status)}`}>
                        {execution.execution_status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="trd-td-muted">{execution.executed_by ? emailToUsername(execution.executed_by) : '—'}</td>
                    <td className="trd-td-muted">{formatTime(execution.executed_at)}</td>
                    <td>
                      {execution.defect_reference
                        ? <span className="trd-defect-link">{execution.defect_reference}</span>
                        : <span className="trd-td-muted">—</span>
                      }
                    </td>
                    <td>
                      {!isImmutable ? (
                        <button
                          className="trd-action-btn"
                          onClick={() => handleExecuteTestCase(execution)}
                          title={execution.execution_status === 'NOT_RUN' ? 'Execute' : 'Re-execute'}
                        >
                          {execution.execution_status === 'NOT_RUN' ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polygon points="5 3 19 12 5 21 5 3"/>
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>
                            </svg>
                          )}
                        </button>
                      ) : (
                        <span className="trd-td-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="trd-pagination">
              <span className="trd-pagination-info">
                Showing {Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} test cases
              </span>
              <div className="trd-pagination-btns">
                <button
                  className="trd-page-btn"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </button>
                <button
                  className="trd-page-btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showExecutionModal && selectedExecution && (
        <TestExecutionModal
          execution={selectedExecution}
          onClose={() => setShowExecutionModal(false)}
          onComplete={handleExecutionComplete}
        />
      )}
    </div>
  );
};

export default TestRunDetailPage;
